import type { CustomVisitPagingVo, Page, ProductPagingParams, ProductStatisticAllParams } from "@zhihaoscm/entity";
import { useNavigate } from "react-router";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps, type TableRequestData } from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";

export interface UserGoodsStatisticsForVisitorDialogProps {
    open: boolean;
    onClose: () => void;
    baseParams?: ProductStatisticAllParams;
    loadRows?: (params: ProductPagingParams & { outerKeyword?: string }) => Promise<Page<CustomVisitPagingVo>>;
}

/** 用户商品访问明细默认接口，沿用旧 Vue custom-visit-paging 契约。 */
export async function loadUserGoodsVisitorStatistics(params: ProductPagingParams & { outerKeyword?: string }) {
    return dashboardService.customVisitPaging(params);
}

/** 组合外层商品筛选条件和访客明细分页条件，保持旧 Vue 请求字段命名。 */
export const buildUserGoodsVisitorStatisticsQuery = (
    rawParams: Record<string, unknown>,
    baseParams?: ProductStatisticAllParams
): ProductPagingParams & { outerKeyword?: string } => {
    const params = rawParams || {};
    const page = String(params.page ?? 1);
    const size = String(params.size ?? 10);
    const { keyword, ...outerParams } = baseParams || {};
    return {
        ...(outerParams as Omit<ProductPagingParams, "page" | "size">),
        ...(params as Omit<ProductPagingParams, "page" | "size">),
        page,
        size,
        scope: outerParams.scope === 6 ? undefined : outerParams.scope,
        ...(keyword ? { outerKeyword: keyword } : {})
    };
};

/** 访问用户明细表列，字段和搜索长度与旧 Vue 保持一致。 */
const createColumns = (navigate: ReturnType<typeof useNavigate>): TableColumnProps<CustomVisitPagingVo>[] => [
    {
        prop: "customerCode",
        label: "用户ID",
        minWidth: 200,
        search: {
            order: 1,
            el: "input",
            props: { placeholder: "请输入用户ID/手机号/个人实名/机构名称搜索", maxlength: 50 },
            key: "keyword"
        },
        render: ({ row }) => (
            <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => navigate(`/lianYunUser/userInfo?userId=${row.customerId ?? ""}`)}
            >
                {row.customerCode || "--"}
            </button>
        )
    },
    { prop: "realName", label: "个人实名", minWidth: 120 },
    { prop: "institutionName", label: "组织机构名称", minWidth: 250 },
    { prop: "mobile", label: "注册手机号", minWidth: 165 }
];

/** 用户商品访问明细弹窗，复用 zhihao-ui-react Table/Dialog 并保留 Vue 的 800 宽度。 */
export function UserGoodsStatisticsForVisitorDialog({
    open,
    onClose,
    baseParams,
    loadRows = loadUserGoodsVisitorStatistics
}: UserGoodsStatisticsForVisitorDialogProps) {
    const navigate = useNavigate();
    const columns = createColumns(navigate);

    const requestRows = async (rawParams: unknown): Promise<TableRequestData<CustomVisitPagingVo>> => {
        const params = (rawParams || {}) as Record<string, unknown>;
        const query = buildUserGoodsVisitorStatisticsQuery(params, baseParams);
        const result = await loadRows(query);
        const page = query.page;
        const size = query.size;
        return { list: result.list || [], currentPage: Number(page), pageSize: Number(size), total: result.total || 0 };
    };

    return (
        <ZhDialog open={open} title="用户商品统计明细" width={800} onCancel={onClose} footer={null}>
            <ZhTable<CustomVisitPagingVo>
                requestApi={requestRows}
                columns={columns}
                rowKey={row => String(row.customerId || row.customerCode || "visitor-row")}
                initParam={{ page: 1, size: 10 }}
                emptyText="暂无数据"
            />
        </ZhDialog>
    );
}

export default UserGoodsStatisticsForVisitorDialog;
